import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import PhotoPlaceholder from "../components/PhotoPlaceholder";
import { Reveal } from "../components/Reveal";

const STRIPES = {
  backgroundImage: "repeating-linear-gradient(115deg, transparent 0 26px, rgba(255,255,255,0.6) 26px 27px)",
};

const FONDATEURS = ["Alexandre Soler", "Grégoire Soler", "Stéphane Roger"];

const EPREUVES = [
  "Pic Estelle VTT", "Cyclo-cross du Boulou", "Cyclo-cross de Saint-André", "Grand Prix de Villemolaque",
  "Grand Prix de Passa", "Grand Prix de Banyuls-dels-Aspres", "L'Enfer des Chartreuses", "Grand Prix du Boulou",
  "Championnat des Pays Catalans", "Gentlemen du Boulou",
];

const TOURS = [
  "Tour du Sénégal — 2005 à 2010", "Tour du Cameroun — 2006 et 2007", "Tour de Tunisie — 2007",
  "Tour du Maroc — 2006 à 2009", "Tour du Québec — 2009", "Tour du Mali — 2010",
];

const GENERATION_FEMININE = [
  {
    name: "Victoria Cushing",
    results:
      "Championne de France cadettes de la course à l'élimination et de la course aux points (2017), vainqueure de la Coupe de France Minimes-Cadettes 2017 et du Chrono des Nations cadettes 2016. Vice-championne de France de vitesse cadettes 2017 et du 500 m juniors 2018, 6e de Gand-Wevelgem Juniors Dames 2018.",
  },
  {
    name: "Pasquine Vandermouten",
    results:
      "Championne de France cadettes de cyclo-cross en 2016 et 2017, vice-championne de France juniors de cyclo-cross 2018, Championne de France juniors de VTT 2019. Plusieurs victoires en Coupe de France de cyclo-cross et de VTT.",
  },
  {
    name: "Clara Tanios",
    results:
      "Championne de France juniors du scratch en 2018, vice-championne de France juniors de la course aux points en 2018.",
  },
  {
    name: "Marie Sonnery-Cottet",
    results: "Plusieurs sélections en équipe de France Junior entre 2014 et 2016.",
  },
];

const TITRES_2021 = [
  { name: "Didier Respaud", text: "Champion de France du contre-la-montre par équipes en 2021." },
  { name: "Alain Gadene", text: "Champion de France Masters en 2021." },
  {
    name: "Francis Roger",
    text: "Figure des générations Masters du club : vice-champion de France Masters sur route en 2011.",
  },
  { name: "Gabriel Roger", text: "Vice-champion de France de la Défense en 2021, vainqueur d'une étape du Tour de la République démocratique du Congo en 2021." },
];

const PROJETS_2026 = [
  { name: "Expresso Racing Women — N2", text: "Création de l'équipe féminine Expresso Racing Women, engagée en Nationale 2." },
  { name: "Expresso Racing — N3", text: "Création de l'équipe masculine Expresso Racing, engagée en Nationale 3." },
  { name: "Team Abadie Magnan", text: "Le projet international se poursuit avec le Team Abadie Magnan, nouvelle étape de l'aventure UCI." },
];

const FIGURES = [
  "Roger Roger", "André Archambaud", "Giani Fontanini", "Famille Billes", "Famille Arche", "Famille Ribalta",
  "La famille Bataller", "Laurent Bailloeuil", "La famille Feytou", "Maria D'Ambrosio", "Famille Roger", "Famille Llagonne",
  "Famille Venzal", "Famille Desnos", "Rostom Bensetti",
  "Famille Respaut", "Patrick De Maura", "Carl Raus", "Jacky Masset",
];

function Chips({ items, dark = false }) {
  return (
    <div className="flex flex-wrap gap-2.5">
      {items.map((item) => (
        <span
          key={item}
          className={`font-heading uppercase tracking-wider text-xs px-3.5 py-2 border ${
            dark ? "border-white/20 text-white/75" : "border-navy/15 text-navy/70"
          }`}
        >
          {item}
        </span>
      ))}
    </div>
  );
}

function Entry({ year, title, major = false, photo = false, children }) {
  return (
    <div className="relative pl-10 sm:pl-16 pb-16 last:pb-0" data-testid={`histoire-entry-${year}`}>
      <span
        className={`absolute left-0 top-2.5 -translate-x-1/2 rounded-full border-4 border-mist ${
          major ? "h-5 w-5 bg-rouge" : "h-4 w-4 bg-navy"
        }`}
        aria-hidden="true"
      />
      <Reveal>
        <span
          className={`block font-heading font-semibold tracking-tighter leading-none ${
            major ? "text-rouge text-5xl sm:text-7xl" : "text-navy text-3xl sm:text-4xl"
          }`}
        >
          {year}
        </span>
        <h3 className="mt-3 font-heading uppercase font-semibold tracking-tight text-navy text-2xl sm:text-3xl">{title}</h3>
        {photo ? (
          <div className="mt-6 grid lg:grid-cols-[1fr_360px] gap-8 items-start">
            <div className="space-y-4 text-navy/80 leading-relaxed">{children}</div>
            <PhotoPlaceholder label="Photo d'archive à venir" className="w-full h-[240px]" />
          </div>
        ) : (
          <div className="mt-6 space-y-4 text-navy/80 leading-relaxed max-w-3xl">{children}</div>
        )}
      </Reveal>
    </div>
  );
}

function Highlight({ year, title, badge = "Moment fort", children }) {
  return (
    <div className="relative pl-10 sm:pl-16 pb-16" data-testid={`histoire-highlight-${title.slice(0, 20)}`}>
      <span className="absolute left-0 top-2.5 -translate-x-1/2 h-5 w-5 rounded-full border-4 border-mist bg-rouge" aria-hidden="true" />
      <Reveal>
        <div className="bg-navy text-white p-8 sm:p-12 relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.06]" style={STRIPES} aria-hidden="true" />
          <span className="relative inline-block bg-rouge text-white text-[10px] font-bold uppercase tracking-[0.25em] px-3 py-1.5 mb-5">
            {badge}
          </span>
          {year && (
            <p className="relative font-heading font-semibold tracking-tighter text-outline-white leading-none text-5xl sm:text-7xl">{year}</p>
          )}
          <h3 className="relative mt-2 font-heading uppercase font-semibold tracking-tight text-2xl sm:text-4xl">{title}</h3>
          <div className="relative mt-5 space-y-4 text-white/75 leading-relaxed max-w-3xl">{children}</div>
        </div>
      </Reveal>
    </div>
  );
}

export default function Histoire() {
  return (
    <>
      <SEO
        title="Notre Histoire"
        description="Depuis 2003, l'histoire du Club Cycliste Le Boulou : compétition internationale, cyclisme féminin, formation des jeunes et projets UCI."
      />
      <PageHeader kicker="Le Club" title="Notre Histoire" />

      <section className="py-16 sm:py-20 border-b border-navy/10">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="font-heading uppercase font-semibold tracking-tight text-rouge text-xl sm:text-2xl">
              Plus de 20 ans de passion, de transmission et de projets.
            </p>
            <div className="mt-7 space-y-4 text-navy/80 text-base sm:text-lg leading-relaxed max-w-3xl">
              <p>Depuis 2003, le Club Cycliste Le Boulou construit son histoire autour d'une même passion : le vélo.</p>
              <p>
                De la compétition internationale à la formation des jeunes, du cyclisme féminin au cyclo-cross, du loisir
                au haut niveau UCI, plusieurs générations de coureurs, de dirigeants, de bénévoles et de familles ont
                contribué à faire grandir le club.
              </p>
              <p>
                Une histoire faite de rencontres, de résultats, de transmission et de projets, toujours profondément
                ancrée dans le territoire du Boulou.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-navy py-16 sm:py-24" data-testid="histoire-femmes-hommes">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="flex items-center gap-4 mb-6">
              <span className="h-px w-12 bg-rouge" />
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-white/60">Depuis 2003</span>
            </div>
            <h2 className="font-heading uppercase font-semibold tracking-tighter leading-[0.95] text-white text-3xl sm:text-5xl">
              Les femmes et les hommes qui ont écrit notre histoire
            </h2>
            <p className="mt-5 text-white/60 max-w-2xl">
              Des générations de dirigeants, éducateurs, bénévoles, coureurs, parents et familles se sont succédé.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="mt-14 mb-5 font-heading uppercase tracking-wider text-sm text-rouge">Les fondateurs</p>
            <div className="grid sm:grid-cols-3 gap-4">
              {FONDATEURS.map((f) => (
                <div key={f} className="border border-white/15 p-6 text-center">
                  <p className="font-heading uppercase font-semibold tracking-tight text-white text-xl">{f}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <p className="mt-12 mb-5 font-heading uppercase tracking-wider text-sm text-rouge">
              Dirigeants, familles et personnes qui ont marqué différentes périodes
            </p>
            <Chips items={FIGURES} dark />
            <p className="mt-4 text-white/40 text-sm italic">… et de nombreuses autres personnes à retrouver.</p>
          </Reveal>

          <Reveal delay={0.2}>
            <blockquote className="mt-16 border-l-4 border-rouge pl-6 sm:pl-10 py-2">
              <p className="text-white/85 text-lg sm:text-xl leading-relaxed italic">
                « Aucun club ne se construit seul. Depuis 2003, des générations de dirigeants, éducateurs, bénévoles,
                coureurs, parents et familles se sont succédé. Certains ont dirigé le club, d'autres ont encadré les
                jeunes, organisé des compétitions, accompagné les équipes, recherché des partenaires ou simplement
                donné de leur temps. Tous ont contribué à écrire l'histoire du Club Cycliste Le Boulou. »
              </p>
            </blockquote>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24" data-testid="histoire-timeline">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="flex items-center gap-4 mb-10">
              <span className="h-px w-12 bg-rouge" />
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-steel">La chronologie</span>
            </div>
          </Reveal>
          <div className="relative border-l-2 border-navy/10 ml-2 sm:ml-4">
            <Entry year="2003" title="La naissance du club" major photo>
              <p>
                En septembre 2003, trois passionnés décident de créer un club consacré au cyclisme sur route.
              </p>
              <div className="flex flex-wrap gap-2.5">
                {FONDATEURS.map((f) => (
                  <span key={f} className="bg-navy text-white font-heading uppercase tracking-wider text-xs px-3.5 py-2">
                    {f}
                  </span>
                ))}
              </div>
              <p>
                Pour lancer leur projet, les trois fondateurs s'appuient sur Freeride 66, alors présidé par Gérard
                Peradejordi. Freeride 66 sert de structure support pour permettre au projet de démarrer, mais le Club
                Cycliste Le Boulou est bien créé à l'initiative des trois fondateurs.
              </p>
              <p>Une vingtaine de personnes rejoignent rapidement cette nouvelle aventure.</p>
            </Entry>

            <Entry year="2004" title="Le club s'installe au Boulou" major photo>
              <p>
                En 2004, le club rejoint la ville du Boulou et installe son siège social à l'Hôtel Le Grillon d'Or.
              </p>
              <p>
                Le club commence à s'affirmer au niveau régional avec plusieurs titres de Champion du
                Languedoc-Roussillon, notamment en contre-la-montre individuel et par équipes. Dès ses premières années,
                il devient également un acteur important de l'organisation des compétitions cyclistes sur le territoire.
              </p>
              <Chips items={EPREUVES} />
              <p className="text-steel text-sm">
                La Gentlemen du Boulou permet notamment de mettre à l'honneur de nombreuses personnalités du cyclisme,
                parmi lesquelles Roger Roger, Roger Élie, André Archambaud et Marcel Lectez.
              </p>
            </Entry>

            <Highlight year="" title="Thor Hushovd" badge="Un parrain prestigieux">
              <p>
                Pendant plusieurs années, le Club Cycliste Le Boulou bénéficie du parrainage de Thor Hushovd, ancien
                champion du monde sur route et ancien porteur du maillot jaune du Tour de France.
              </p>
              <p>
                Le champion norvégien vit au Boulou pendant une dizaine d'années. Sa présence crée un lien particulier
                entre le club, la commune et le monde du très haut niveau cycliste.
              </p>
            </Highlight>

            <Entry year="2005–2010" title="L'ouverture internationale" major>
              <p>À partir de 2005, le club développe un véritable projet de compétition internationale.</p>
              <Chips items={TOURS} />
              <p>
                Cette période marque le passage d'un club principalement régional à une structure capable de participer
                à des compétitions nationales et internationales.
              </p>
            </Entry>

            <Highlight year="2005–2007" title="Marcel Lectez, triple champion du monde">
              <p>Marcel Lectez devient l'une des grandes figures sportives du club, avec trois titres consécutifs :</p>
              <Chips items={["Champion du monde Masters 2005", "Champion du monde Masters 2006", "Champion du monde Masters 2007"]} dark />
              <p>Trois titres mondiaux consécutifs : l'un des grands moments de l'histoire sportive du club.</p>
            </Highlight>

            <Highlight year="2007" title="Flavien Chipault et le Tour du Cameroun">
              <p>
                En 2007, Flavien Chipault remporte le classement général du Tour du Cameroun sous les couleurs du Club
                Cycliste Le Boulou. Une victoire internationale majeure pour le club.
              </p>
            </Highlight>

            <Entry year="2007–2008" title="L'entente avec Saint-Chély-d'Apcher">
              <p>
                Le club poursuit sa structuration sportive avec l'UC Saint-Chély-d'Apcher. En 2007 apparaît la
                structure UC Saint-Chély-d'Apcher – CC Le Boulou, qui évolue en 2008 sous la forme UC
                Saint-Chély-d'Apcher – CC Le Boulou – LAR.
              </p>
              <p>Cette période participe au développement des équipes de niveau DN2 et DN3.</p>
            </Entry>

            <Entry year="2008–2009" title="L'Entente Sud Méditerranée">
              <p>
                Le club poursuit son développement au niveau national à travers différentes structures et ententes,
                notamment l'Entente Sud Méditerranée, qui permet de poursuivre la structuration d'équipes compétitives
                au niveau national.
              </p>
            </Entry>

            <Entry year="2009" title="Une année historique" major photo>
              <p>
                Stéphane Roger remporte le classement général du Tour du Sénégal sous les couleurs du club. La même
                année, il termine 3e du Championnat du monde Masters.
              </p>
              <p>Le club participe également au Tour du Québec.</p>
            </Entry>

            <Entry year="2010–2014" title="La formation au cœur du projet" major>
              <p>
                À partir de 2010, le club renforce son travail autour de l'école de vélo et de la formation des jeunes,
                avec l'objectif de construire une école capable de devenir une référence régionale.
              </p>
              <p>Le travail entrepris pendant plusieurs années porte ses fruits.</p>
            </Entry>

            <Highlight year="2014" title="Champion du Languedoc-Roussillon des écoles de vélo">
              <p>
                En 2014, le Club Cycliste Le Boulou devient Champion du Languedoc-Roussillon des écoles de vélo à
                Mende.
              </p>
            </Highlight>

            <Entry year="2012" title="Le tournant du cyclisme féminin" major>
              <p>
                2012 marque une nouvelle étape majeure : le club s'engage dans le développement du cyclisme féminin
                avec le Team Féminin Languedoc-Roussillon.
              </p>
              <p>
                Le projet féminin va progressivement devenir l'un des piliers du club et conduire à plusieurs années au
                plus haut niveau national.
              </p>
            </Entry>

            <Entry year="2013" title="Formation et cyclo-cross">
              <p>
                À partir de 2013, le club développe également son activité de cyclo-cross chez les jeunes, qui
                participent notamment aux manches de Coupe de France.
              </p>
              <p>Le développement du cyclo-cross et celui du projet féminin se poursuivent en parallèle.</p>
            </Entry>

            <Highlight year="2015" title="Manon Souyris et la Coupe de France">
              <p>
                En 2015, Manon Souyris remporte la Coupe de France féminine — un résultat majeur dans la construction
                du projet féminin.
              </p>
              <p>
                La même année, Emma Roger termine 10e de la Coupe de France de cyclo-cross. Avec ses frères Gabriel et
                Romain Roger, elle forme une véritable « trilogie des Roger » au sein du club.
              </p>
            </Highlight>

            <Entry year="2016–2019" title="Une génération féminine au plus haut niveau" major photo>
              <p>
                Le travail de formation et de développement du cyclisme féminin produit plusieurs résultats majeurs,
                portés par une génération de jeunes coureuses qui ont marqué l'histoire du club.
              </p>
              <div className="grid sm:grid-cols-2 gap-4 not-prose">
                {GENERATION_FEMININE.map((p) => (
                  <div key={p.name} className="bg-white border border-navy/10 p-6">
                    <p className="font-heading uppercase font-semibold tracking-tight text-navy text-lg">{p.name}</p>
                    <p className="mt-2 text-sm text-steel leading-relaxed">{p.results}</p>
                  </div>
                ))}
              </div>
            </Entry>

            <Entry year="2017" title="Team Féminin Le Boulou" major>
              <p>
                En 2017, le projet prend une nouvelle identité : Team Féminin Le Boulou. Le projet féminin est
                désormais directement associé au nom du club.
              </p>
            </Entry>

            <Entry year="2017–2018" title="Gabriel Roger, meilleur sprinteur du Tour du Lac Léman">
              <p>
                En 2017 puis en 2018, Gabriel Roger remporte à deux reprises le maillot de meilleur sprinteur du Tour
                du Lac Léman.
              </p>
            </Entry>

            <Entry year="2021" title="De nouveaux titres nationaux" major>
              <p>Le club continue à s'illustrer dans différentes catégories.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {TITRES_2021.map((p) => (
                  <div key={p.name} className="bg-white border border-navy/10 p-6">
                    <p className="font-heading uppercase font-semibold tracking-tight text-navy text-lg">{p.name}</p>
                    <p className="mt-2 text-sm text-steel leading-relaxed">{p.text}</p>
                  </div>
                ))}
              </div>
            </Entry>

            <Highlight year="2022" title="L'arrivée du Groupe Abadie : un tournant">
              <p>
                2022 marque un tournant majeur dans l'histoire du club. L'arrivée du Groupe Abadie donne une nouvelle
                dimension au projet sportif : l'équipe féminine évolue sous le nom Team Abadie Le Boulou et termine 3e
                du classement par équipes de la Coupe de France féminine.
              </p>
              <p>
                Le partenariat permet surtout d'envisager une nouvelle étape : le passage au niveau UCI. L'arrivée du
                Groupe Abadie ne représente donc pas simplement un nouveau partenariat — elle ouvre une nouvelle page
                de l'histoire du club.
              </p>
              <p>
                La même année, Francis Roger et Emmanuel Lieutier deviennent Champions de France Masters par équipes
                sous les couleurs de l'Occitanie à Crocq.
              </p>
              <p>
                2022, c'est aussi la sélection de Noémie Daumas et Ilona Feytou au championnat du monde junior femme.
              </p>
            </Highlight>

            <Highlight year="2023" title="Première année UCI">
              <p>
                En 2023, le projet franchit une étape historique avec la création du Team Groupe Abadie : c'est la
                première année UCI du projet.
              </p>
              <p>Le club entre ainsi dans une nouvelle dimension sportive et internationale.</p>
            </Highlight>

            <Entry year="2024" title="Team Primeau Vélo – Groupe Abadie" major>
              <p>
                En 2024, le projet UCI se poursuit avec le Team Primeau Vélo – Groupe Abadie. Le club continue son
                développement international.
              </p>
            </Entry>

            <Entry year="2025" title="Création du Team Expresso Racing" major>
              <p>
                2025 voit la création du Team Expresso Racing, nouveau projet sportif du club, tout en assurant la
                continuité des activités existantes et la préparation de la suite du projet.
              </p>
            </Entry>

            <Highlight year="2026" title="Une nouvelle organisation">
              <p>2026 marque une nouvelle étape : trois projets sportifs structurent désormais le club.</p>
              <div className="grid sm:grid-cols-3 gap-4">
                {PROJETS_2026.map((p) => (
                  <div key={p.name} className="bg-white/5 border border-white/10 p-5">
                    <p className="font-heading uppercase font-semibold tracking-tight text-white text-base">{p.name}</p>
                    <p className="mt-2 text-sm text-white/60 leading-relaxed">{p.text}</p>
                  </div>
                ))}
              </div>
            </Highlight>

            <div className="relative pl-10 sm:pl-16" data-testid="histoire-2027">
              <span className="absolute left-0 top-2.5 -translate-x-1/2 h-5 w-5 rounded-full border-4 border-mist bg-rouge" aria-hidden="true" />
              <Reveal>
                <div className="bg-rouge text-white p-8 sm:p-12">
                  <p className="font-heading font-semibold tracking-tighter leading-none text-5xl sm:text-7xl text-white/30">2027</p>
                  <h3 className="mt-2 font-heading uppercase font-semibold tracking-tight text-2xl sm:text-4xl">La suite s'écrit ensemble</h3>
                  <p className="mt-5 text-white/85 leading-relaxed max-w-2xl">
                    Une nouvelle page à écrire avec celles et ceux qui font vivre le club : coureurs et coureuses,
                    jeunes de l'école de vélo, bénévoles, familles et partenaires.
                  </p>
                  <Link
                    to="/contact"
                    data-testid="histoire-2027-cta"
                    className="group mt-7 inline-flex items-center gap-3 bg-navy text-white font-heading uppercase tracking-wider text-sm px-7 py-4 hover:bg-white hover:text-navy transition-[background-color,color] duration-300"
                  >
                    Écrire la suite avec nous
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

    </>
  );
}
